import type { DesignsToken } from '@repo/tokens-studio';
import Color from 'color';

import { ThemeColorKey } from '@web/ui/theme/type';

/**
 * This utility function converts box-shadow value from TS to correct text-shadow.
 *
 */
export function getTextShadow(boxShadow: string) {
  return boxShadow
    .split(' ')
    .filter((_, index) => index !== 3)
    .join(' ');
}

/**
 * This utility function allows to build CSS border from the theme.
 * Usage:
 *
 * styled.div`
 *   some-css-prop: some-css-value;
 *   ${getBorder('1', 'primary', '0500', 'solid')}
 * `;
 *
 */
export function getBorder(
  width: keyof DesignsToken['outline'],
  color: ThemeColorKey,
  radius: keyof DesignsToken['shape']['corner'],
  style = 'solid',
) {
  return ({
    theme,
  }: {
    theme: Pick<DesignsToken, 'outline' | 'color' | 'shape'>;
  }) => `
    border-width: ${theme.outline[width]};
    border-color: ${theme.color[color]};
    border-radius: ${theme.shape.corner[radius]};
    border-style: ${style};
  `;
}

/**
 * This utility function allows to emulate CSS text stroke from the theme
 * Usage:
 *
 * styled.div`
 *   some-css-prop: some-css-value;
 *   ${getTextStroke('1', 'primary')}
 * `;
 *
 */
export function getTextStroke(
  width: keyof DesignsToken['outline'],
  color: keyof DesignsToken['color'],
) {
  return ({
    theme,
  }: {
    theme: Pick<DesignsToken, 'outline' | 'color' | 'shape'>;
  }) =>
    `
    text-shadow:
      -${theme.outline[width]} -${theme.outline[width]} 0 ${theme.color[color]},
       0        -${theme.outline[width]} 0 ${theme.color[color]},
       ${theme.outline[width]} -${theme.outline[width]} 0 ${theme.color[color]},
       ${theme.outline[width]}  0        0 ${theme.color[color]},
       ${theme.outline[width]}  ${theme.outline[width]} 0 ${theme.color[color]},
       0         ${theme.outline[width]} 0 ${theme.color[color]},
      -${theme.outline[width]}  ${theme.outline[width]} 0 ${theme.color[color]},
      -${theme.outline[width]}  0        0 ${theme.color[color]};  
  `;
}

export function getStateLayerColor(
  color: keyof DesignsToken['color'],
  stateOrOpacity: 'hover' | 'active' | keyof DesignsToken['opacity'],
) {
  return ({ theme }: { theme: Pick<DesignsToken, 'color' | 'opacity'> }) => {
    const opacity = (() => {
      if (stateOrOpacity in theme.opacity)
        return stateOrOpacity as keyof DesignsToken['opacity'];
      return stateOrOpacity === 'hover' ? 'semiTransparent' : 'faded';
    })();

    return `linear-gradient(${Color(theme.color[color]).alpha(theme.opacity[opacity])},${Color(theme.color[color]).alpha(theme.opacity[opacity])})`;
  };
}
